/**
 * Realtime client — replaces `supabase.channel(…)`.
 *
 * The app uses a small, stable subset (measured): three channel-name patterns,
 * presence (sync/join/leave), one broadcast event, and `postgres_changes` on
 * three tables. This reproduces that subset over Socket.IO against
 * `apps/api/src/realtime/`, keeping the call shapes so migrating a hook is a
 * rename rather than a rewrite:
 *
 *     supabase.channel("session:" + id)  →  realtime.channel("session:" + id)
 *
 * Semantics preserved deliberately:
 * - `presenceState()` is the source of truth and is replayed on join, because
 *   `SessionChannelContext` reads it directly to decide who is in the room.
 * - `broadcast` does not echo to the sender, matching Supabase.
 * - `postgres_changes` handlers receive `{ eventType, new, old }`.
 */

import { io, type Socket } from "socket.io-client";

/**
 * Status values are supabase-js's literals, not our own vocabulary: the call
 * sites switch on "SUBSCRIBED" / "CHANNEL_ERROR" / "TIMED_OUT" to show connection
 * banners, and renaming them would silently disable those messages.
 */
type ChannelState = "SUBSCRIBED" | "CHANNEL_ERROR" | "TIMED_OUT" | "CLOSED";

type PresenceHandler = (payload: { newPresences?: Record<string, unknown>[]; leftPresences?: Record<string, unknown>[] }) => void;
type BroadcastHandler = (payload: { payload: unknown }) => void;
type PostgresHandler = (payload: {
  eventType: "INSERT" | "UPDATE" | "DELETE";
  new: Record<string, unknown>;
  old: Record<string, unknown>;
  truncated?: boolean;
}) => void;

interface Subscription {
  type: "presence" | "broadcast" | "postgres_changes";
  event?: string;
  table?: string;
  filter?: { column: string; value: string };
  handler: PresenceHandler | BroadcastHandler | PostgresHandler;
}

function apiBaseUrl(): string | null {
  const value = import.meta.env.VITE_API_BASE_URL as string | undefined;
  return value ? value.replace(/\/+$/, "") : null;
}

function accessToken(): string | null {
  try {
    const raw = localStorage.getItem("lyder-talk-auth:v1");
    if (!raw) return null;
    return (JSON.parse(raw) as { access_token?: string }).access_token ?? null;
  } catch {
    return null;
  }
}

/** One socket per page. Channels multiplex over it, like Supabase's. */
let socket: Socket | null = null;
/**
 * room name → every channel object subscribed to it.
 *
 * A Set, not a single channel: two components can legitimately watch the same
 * room (`SessionChannelContext` owns `session:<id>` for presence, and the
 * comments list reads `postgres_changes` on the same room). Keying by name and
 * storing one object meant the second subscription silently replaced the first,
 * so whichever mounted last received the events.
 */
const channels = new Map<string, Set<RealtimeChannel>>();

function channelsFor(name: string): Set<RealtimeChannel> {
  return channels.get(name) ?? new Set<RealtimeChannel>();
}

/** Fan one server event out to every channel object subscribed to that room. */
function forEachChannel(name: string, apply: (channel: RealtimeChannel) => void): void {
  for (const channel of channelsFor(name)) apply(channel);
}

function sharedSocket(): Socket | null {
  if (socket) return socket;

  const base = apiBaseUrl();
  if (!base) return null;

  socket = io(base, {
    transports: ["websocket"],
    auth: { token: accessToken() },
    // Reconnect aggressively: a study session can sit idle, and a dropped socket
    // looks identical to "nobody else is here".
    reconnection: true,
    reconnectionDelay: 500,
    reconnectionDelayMax: 5_000,
  });

  socket.on("presence:sync", ({ channel, state }: { channel: string; state: Record<string, Record<string, unknown>[]> }) => {
    forEachChannel(channel, (target) => target.applyPresenceSync(state));
  });
  socket.on("presence:join", ({ channel, newPresences }: { channel: string; newPresences: Record<string, unknown>[] }) => {
    forEachChannel(channel, (target) => target.applyPresenceJoin(newPresences));
  });
  socket.on("presence:leave", ({ channel, leftPresences }: { channel: string; leftPresences: Record<string, unknown>[] }) => {
    forEachChannel(channel, (target) => target.applyPresenceLeave(leftPresences));
  });
  socket.on("broadcast", ({ channel, event, payload }: { channel: string; event: string; payload: unknown }) => {
    forEachChannel(channel, (target) => target.applyBroadcast(event, payload));
  });
  socket.on(
    "postgres_changes",
    ({ channel, table, eventType, new: row, old }: { channel: string; table: string; eventType: string; new: Record<string, unknown>; old: Record<string, unknown> }) => {
      forEachChannel(channel, (target) =>
        target.applyPostgresChange(table, eventType as PostgresHandler extends never ? never : "INSERT" | "UPDATE" | "DELETE", row, old),
      );
    },
  );

  // Re-join everything after a reconnect, otherwise a channel looks alive but
  // receives nothing.
  socket.on("connect", () => {
    for (const group of channels.values()) {
      for (const channel of group) channel.rejoin();
    }
  });

  return socket;
}

export class RealtimeChannel {
  private subscriptions: Subscription[] = [];
  private presence = new Map<string, Record<string, unknown>[]>();
  private state: ChannelState = "CLOSED";
  private statusCallback?: (status: ChannelState) => void;

  constructor(readonly name: string) {}

  /* ---------------------------------------------------------- registration */

  on(
    type: "presence" | "broadcast" | "postgres_changes",
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    filterOrHandler: { event?: string; schema?: string; table?: string; filter?: string } | ((payload: any) => void),
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    maybeHandler?: (payload: any) => void,
  ): this {
    const handler = (typeof filterOrHandler === "function" ? filterOrHandler : maybeHandler) as
      | Subscription["handler"]
      | undefined;
    if (!handler) return this;

    if (typeof filterOrHandler === "function") {
      this.subscriptions.push({ type, handler });
      return this;
    }

    const subscription: Subscription = { type, handler };

    if (type === "presence") subscription.event = filterOrHandler.event ?? "sync";
    if (type === "broadcast") subscription.event = filterOrHandler.event;
    if (type === "postgres_changes") {
      // `schema` is accepted for API compatibility and ignored: the gateway only
      // exposes `public`, and accepting an arbitrary schema would imply a
      // capability that does not exist.
      subscription.table = filterOrHandler.table;
      // "practice_session_id=eq.<uuid>" → { column, value }
      const match = /^([a-z_]+)=eq\.(.+)$/i.exec(filterOrHandler.filter ?? "");
      if (match) subscription.filter = { column: match[1], value: match[2] };
    }

    this.subscriptions.push(subscription);
    return this;
  }

  subscribe(callback?: (status: ChannelState) => void): this {
    this.statusCallback = callback;
    const group = channelsFor(this.name);
    group.add(this);
    channels.set(this.name, group);

    const connection = sharedSocket();
    if (!connection) {
      this.setStatus("CHANNEL_ERROR");
      return this;
    }

    const joinNow = () => connection.emit("join", { channel: this.name });
    if (connection.connected) joinNow();
    else connection.once("connect", joinNow);

    this.setStatus("SUBSCRIBED");
    return this;
  }

  /** Re-join after a reconnect. */
  rejoin(): void {
    if (this.state === "SUBSCRIBED") sharedSocket()?.emit("join", { channel: this.name });
  }

  async track(payload: object, _options?: { key?: string }): Promise<void> {
    // `key` grouping is a server concern here (presence is keyed by socket), so
    // it is accepted for API compatibility and ignored.
    sharedSocket()?.emit("track", { channel: this.name, payload });
  }

  async untrack(): Promise<void> {
    sharedSocket()?.emit("untrack", { channel: this.name });
  }

  /** `send({ type: "broadcast", event, payload })`. */
  async send(message: { type: string; event: string; payload?: unknown }): Promise<void> {
    if (message.type !== "broadcast") {
      throw new Error(`realtime: unsupported send type "${message.type}"`);
    }
    sharedSocket()?.emit("broadcast", { channel: this.name, event: message.event, payload: message.payload });
  }

  unsubscribe(): void {
    const group = channels.get(this.name);
    group?.delete(this);
    // Only leave the room when nothing else is listening to it: leaving while
    // another channel object still holds the same room would cut that one off
    // from presence and database events.
    if (!group || group.size === 0) {
      channels.delete(this.name);
      sharedSocket()?.emit("leave", { channel: this.name });
    }
    this.subscriptions = [];
    this.presence.clear();
    this.setStatus("CLOSED");
  }

  /** Current presence snapshot, keyed by presence ref — same shape as Supabase. */
  presenceState<T = Record<string, unknown>>(): Record<string, T[]> {
    return Object.fromEntries(this.presence) as Record<string, T[]>;
  }

  /* --------------------------------------------------------- server events */

  applyPresenceSync(state: Record<string, Record<string, unknown>[]>): void {
    this.presence = new Map(Object.entries(state));
    this.fire(this.subscriptions.filter((s) => s.type === "presence" && s.event === "sync"), {});
  }

  applyPresenceJoin(newPresences: Record<string, unknown>[]): void {
    // The gateway sends entries, not refs. Key by the socket-derived userId when
    // available so a re-track replaces rather than duplicates.
    for (const entry of newPresences) {
      const key = String(entry.userId ?? entry.socketId ?? Math.random());
      const existing = this.presence.get(key) ?? [];
      this.presence.set(key, [...existing.filter((item) => item.userId !== entry.userId), entry]);
    }
    this.fire(this.subscriptions.filter((s) => s.type === "presence" && s.event === "join"), { newPresences });
  }

  applyPresenceLeave(leftPresences: Record<string, unknown>[]): void {
    for (const entry of leftPresences) {
      for (const [key, entries] of this.presence) {
        const remaining = entries.filter((item) => item.userId !== entry.userId);
        if (remaining.length === 0) this.presence.delete(key);
        else this.presence.set(key, remaining);
      }
    }
    this.fire(this.subscriptions.filter((s) => s.type === "presence" && s.event === "leave"), { leftPresences });
  }

  applyBroadcast(event: string, payload: unknown): void {
    this.fire(
      this.subscriptions.filter((s) => s.type === "broadcast" && s.event === event),
      { payload },
    );
  }

  applyPostgresChange(
    table: string,
    eventType: "INSERT" | "UPDATE" | "DELETE",
    row: Record<string, unknown>,
    old: Record<string, unknown>,
  ): void {
    const handlers = this.subscriptions.filter((subscription) => {
      if (subscription.type !== "postgres_changes" || subscription.table !== table) return false;
      if (!subscription.filter) return true;
      // The server fans out by room; the filter still has to be applied per
      // subscription, because one room can carry several tables (session:<id>
      // receives both comments and chat).
      const source = row?.[subscription.filter.column] ?? old?.[subscription.filter.column];
      return String(source) === subscription.filter.value;
    });

    this.fire(handlers, { eventType, new: row, old });
  }

  private fire(subscriptions: Subscription[], payload: unknown): void {
    for (const subscription of subscriptions) {
      try {
        (subscription.handler as (value: unknown) => void)(payload);
      } catch (error) {
        console.error(`realtime handler for ${this.name} threw`, error);
      }
    }
  }

  private setStatus(status: ChannelState): void {
    this.state = status;
    this.statusCallback?.(status);
  }
}

export const realtime = {
  channel(name: string): RealtimeChannel {
    return new RealtimeChannel(name);
  },
  removeChannel(channel: RealtimeChannel): void {
    channel.unsubscribe();
  },
};
